import type { UserRole } from '@coreltb/shared/constants';
import { isActionableNotification } from '@coreltb/shared/constants';
import type { Notification } from '@coreltb/shared/types';

/**
 * Karty klienta (Dawid 2026-10-01): wszystko o jednym kliencie razem — przypomnienie,
 * reakcja na ofertę, notatka kolegi — zamiast kilku luźnych wpisów. Powiadomienia
 * bez klienta (projekty, uwagi, rabaty) zostają pojedyncze.
 */
export interface NotificationGroup {
  key: string;
  leadId: string | null;
  leadName: string | null;
  /** Najważniejsze na górze: do zrobienia, potem informacje, na końcu załatwione. */
  items: Notification[];
  latestAt: number;
  unresolvedIds: string[];
  hasNew: boolean;
}

function rank(n: Notification, role: UserRole | null): number {
  if (n.resolvedAt) return 2;
  return isActionableNotification(n.type, role) ? 0 : 1;
}

export function groupNotifications(
  items: readonly Notification[],
  role: UserRole | null,
): NotificationGroup[] {
  const groups = new Map<string, NotificationGroup>();
  for (const n of items) {
    const key = n.leadId ? `lead:${n.leadId}` : `one:${n.id}`;
    const g = groups.get(key) ?? {
      key,
      leadId: n.leadId ?? null,
      leadName: n.leadName ?? null,
      items: [],
      latestAt: 0,
      unresolvedIds: [],
      hasNew: false,
    };
    g.items.push(n);
    g.latestAt = Math.max(g.latestAt, n.createdAt);
    if (!n.resolvedAt) g.unresolvedIds.push(n.id);
    if (!n.readAt && !n.resolvedAt) g.hasNew = true;
    groups.set(key, g);
  }
  for (const g of groups.values()) {
    g.items.sort((a, b) => rank(a, role) - rank(b, role) || b.createdAt - a.createdAt);
  }
  return [...groups.values()].sort((a, b) => b.latestAt - a.latestAt);
}

/**
 * Co zrobić po kliknięciu (jak przy pojedynczym powiadomieniu): rzeczy do zrobienia
 * zostają w liczniku („przeczytane”), informacje od razu znikają („załatwione”).
 */
export function openPlan(
  items: readonly Notification[],
  role: UserRole | null,
): { read: string[]; resolve: string[] } {
  const read: string[] = [];
  const resolve: string[] = [];
  for (const n of items) {
    if (n.resolvedAt) continue;
    if (!isActionableNotification(n.type, role)) resolve.push(n.id);
    else if (!n.readAt) read.push(n.id);
  }
  return { read, resolve };
}

/** Klik w klienta (całą kartę): tylko „widziałem” — nic nie zamyka, odhacza handlowiec. */
export function seenPlan(items: readonly Notification[]): { read: string[] } {
  return { read: items.filter((n) => !n.readAt && !n.resolvedAt).map((n) => n.id) };
}

/**
 * Zakładka „Do akcji”: do otwartych kart dokładamy niedawno odhaczone pozycje tych samych
 * klientów — karta pokazuje, co już zrobione, a co jeszcze nie (Dawid 2026-10-01: „drabinka”).
 */
export function withDoneOfOpenClients(
  todo: readonly Notification[],
  done: readonly Notification[],
): Notification[] {
  const open = new Set(todo.flatMap((n) => (n.leadId ? [n.leadId] : [])));
  // Tuż po odhaczeniu pozycja bywa jeszcze w obu listach (odświeżają się osobno) — wygrywa „zrobione”.
  const doneIds = new Set(done.map((n) => n.id));
  return [
    ...todo.filter((n) => !doneIds.has(n.id)),
    ...done.filter((n) => n.leadId && open.has(n.leadId)),
  ];
}
